'use client'

import { useRef, type ClipboardEvent, type KeyboardEvent } from 'react'
import { cn } from '@utils/cn'

const CODE_LENGTH = 6

interface CodeInputProps {
  value: string
  onChange: (code: string) => void
  disabled?: boolean
  error?: boolean
}

/** Поле ввода 6-значного кода: по одной ячейке на цифру, с автопереходом фокуса. */
export const CodeInput = ({ value, onChange, disabled, error }: CodeInputProps) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([])

  const focusAt = (index: number) => inputsRef.current[index]?.focus()

  const setDigitAt = (index: number, digit: string) => {
    const digits = value.split('')
    digits[index] = digit
    onChange(digits.join('').slice(0, CODE_LENGTH))
  }

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1)
    setDigitAt(index, digit)
    if (digit && index < CODE_LENGTH - 1) {
      focusAt(index + 1)
    }
  }

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    // Backspace в пустой ячейке — возвращаемся к предыдущей.
    if (event.key === 'Backspace' && !value[index] && index > 0) {
      focusAt(index - 1)
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH)
    if (!pasted) {
      return
    }

    onChange(pasted)
    focusAt(Math.min(pasted.length, CODE_LENGTH - 1))
  }

  return (
    <div className="flex justify-center gap-2 sm:gap-3" onPaste={handlePaste}>
      {Array.from({ length: CODE_LENGTH }).map((_, index) => (
        <input
          key={index}
          ref={(element) => {
            inputsRef.current[index] = element
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          autoComplete="one-time-code"
          autoFocus={index === 0}
          disabled={disabled}
          value={value[index] ?? ''}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          className={cn(
            'h-14 w-12 rounded-lg border text-center text-2xl font-semibold outline-none transition-colors',
            'focus:border-blue-400 disabled:opacity-50 dark:bg-gray-700 dark:text-white',
            error ? 'border-red-500' : 'border-gray-200 dark:border-gray-600'
          )}
        />
      ))}
    </div>
  )
}
